import { exception } from 'circe/logger';
import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import {
  all, takeLatest, call, select, put,
} from 'redux-saga/effects';
import * as I18n from 'circe/I18n';
import { show as showNotification } from 'circe/notifications/actions';

import {
  morphReassignToQuotes,
  geDriversFetchPayloadUsingShipment,
  morphVehiclesToDrivers,
} from './utils';
import * as api from './api';
import {
  fetch,
  fetchNext,
  fetchPrev,
  fetching,
  fetched,
  reassign,
  reassigned,
  fetchDrivers,
  fetchedDrivers,
  manualDriverSelect,
} from './actions';

import {
  getNextCursor, getHasNext, getHasPrev, getPrevCursor,
} from './selectors';

function* doFetch(context, { payload }) {
  try {
    const { shipment } = payload;

    const state = yield select();

    const headers = yield call(getCurrentAuthHeader, state);

    const resp = yield call(
      api.automaticFetch,
      geDriversFetchPayloadUsingShipment(shipment),
      headers,
    );

    const results = [];

    const drivers = morphVehiclesToDrivers(resp.results);

    const maxResults = drivers.length > 6 ? 6 : drivers.length;

    let c = 0;

    while (c < maxResults) {
      const resp2 = yield call(
        api.manualFetch,
        {
          resources: {
            driverId: drivers[c].id,
            fleetId: drivers[c].fleetId,
            truckId: drivers[c].vehicle.id,
          },
          shipments: [shipment.shipmentId],
        },
        headers,
      );

      results.push(morphReassignToQuotes(resp2, drivers[c], state));

      c++;
    }

    yield put(fetched(results));
  } catch (e) {
    yield put(
      showNotification(I18n.str('shipmentsFetchFailed', 'Unable to load shipments.'), 'error'),
    );
    yield put(fetched());
    exception(e);
  }
}

export function* doFetchNext(context) {
  const state = yield select();

  const hasNextPage = yield call(getHasNext, state);

  if (!hasNextPage) {
    return;
  }

  const cursor = yield call(getNextCursor, state);

  yield put(fetching());

  yield call(doFetch, context, fetch({ cursor }));
}

export function* doFetchPrev(context) {
  const state = yield select();

  const hasPrevPage = yield call(getHasPrev, state);

  if (!hasPrevPage) {
    return;
  }

  const cursor = yield call(getPrevCursor, state);

  yield put(fetching());

  yield call(doFetch, context, fetch({ cursor }));
}

function* doReassign(context, { payload }) {
  const {
    itineraryId, shipmentId, driver, vehicle,
  } = payload;

  const state = yield select();

  const headers = yield call(getCurrentAuthHeader, state);

  yield call(api.reassign, itineraryId, headers);

  yield put(
    reassigned({
      shipmentId,
      itineraryId,
      driver,
      vehicle,
    }),
  );

  yield put(
    showNotification(
      I18n.str('shipmentReassignSuccess', 'Shipment reassigned successfully.'),
      'success',
    ),
  );
}

function* doFetchDrivers(context, { payload }) {
  const { shipment } = payload;

  const state = yield select();

  const headers = yield call(getCurrentAuthHeader, state);

  const resp = yield call(
    api.fetchDrivers,
    {
      sender: shipment.sender,
      senderId: shipment.senderId,
      senderPhone: shipment.senderPhone,

      receiver: shipment.receiver,
      receiverPhone: shipment.receiverPhone,

      load: shipment.load,
      accessorials: shipment.accessorials,
      pickup: {
        latitude: shipment.pickup.coordinate.latitude,
        longitude: shipment.pickup.coordinate.longitude,
        time: shipment.pickup.arrivalWindow.begin,
        // time: moment(shipment.dropoff.arrivalWindow.begin).add(20,'days').toISOString(),
      },
      dropoff: {
        latitude: shipment.dropoff.coordinate.latitude,
        longitude: shipment.dropoff.coordinate.longitude,
        // time: moment(shipment.dropoff.arrivalWindow.begin).add(20,'days').toISOString(),
      },
    },
    headers,
  );

  yield put(
    fetchedDrivers(
      resp.results.reduce(
        (acc, result) => [
          ...acc,
          ...result.drivers.map((driver) => ({
            ...driver,
            fleetId: result.fleetId,
            vehicle: result.vehicle,
          })),
        ],
        [],
      ),
    ),
  );
}

function* doManualDriverSelect(context, { payload }) {
  const { driver, shipmentId } = payload;

  const state = yield select();

  const headers = yield call(getCurrentAuthHeader, state);

  yield put(fetching());

  const vehicle = (driver.vehicle ? driver.vehicle : driver.vehicles[driver.vehicles.length - 1]) || {};

  const resp = yield call(
    api.manualFetch,
    {
      resources: {
        driverId: driver.id,
        fleetId: driver.fleets[0].id,
        truckId: vehicle.id,
      },
      shipments: [shipmentId],
    },
    headers,
  );

  const quotes = [morphReassignToQuotes(resp, driver, state)];

  yield put(fetched(quotes));
}

export default function* (context) {
  yield all([
    takeLatest(fetch().type, doFetch, context),
    takeLatest(fetchNext().type, doFetchNext, context),
    takeLatest(fetchPrev().type, doFetchPrev, context),
    takeLatest(reassign().type, doReassign, context),
    takeLatest(fetchDrivers().type, doFetchDrivers, context),
    takeLatest(manualDriverSelect().type, doManualDriverSelect, context),
  ]);
}
